import { useState } from "react";

import { useTranslator } from "@eo-locale/react";

import {
  Field,
  HTMLWrapper,
  VisuallyHidden,
  HStack,
  useRadio,
  Box,
  useRadioGroup,
  UseRadioProps,
  ImageEditor
} from "@/storybook-components";
import type { Question } from "../question";

interface ColourOrImageQuestionProps extends Question {
  allowImageUpload: boolean;
  onColourChangeHandler: (colourId: number) => void;
  onImageChangeHandler: (imageUrl: string) => void;
  backgroundColours: Array<{ name: string; id: number }>;
  existingIdOrUrl: { color: number; image: string };
}

const getColourForHover = (hex: string): string => {
  const colours: { [key: string]: string } = {
    "#975A16": "#744210",
    "#2C7A7B": "#285E61",
    "#B83280": "#97266D",
    "#6B46C1": "#553C9A"
  };

  if (colours[hex]) return colours[hex];

  return hex;
};

function RadioOption(
  props: UseRadioProps & { background: string; dataTestId?: string }
) {
  const { background, dataTestId, ...rest } = props;
  const checked = Boolean(rest.isChecked);
  const { getInputProps, getRadioProps } = useRadio(rest);

  const input = getInputProps();
  const radio = getRadioProps();
  const circleSize = { base: 10, md: 8 };

  return (
    <Box
      as="label"
      position="relative"
      display="inline-flex"
      flexDirection="column"
      alignItems="center"
    >
      <input {...input} title={background} />
      <Box
        {...radio}
        aria-hidden={true}
        cursor="pointer"
        data-testid={dataTestId}
        borderRadius="full"
        backgroundColor={background}
        w={circleSize}
        h={circleSize}
        flexShrink={0}
        _hover={{
          backgroundColor: getColourForHover(background)
        }}
        _focus={{
          boxShadow: "outline"
        }}
        _disabled={{
          opacity: 0.4,
          boxShadow: "none",
          cursor: "not-allowed",
          userSelect: "none",
          _hover: { backgroundColor: background }
        }}
      />
      {checked && (
        <Box
          position="absolute"
          bottom="-1.25rem"
          h="4px"
          w={circleSize}
          bg="brand.500"
          flexShrink={0}
        />
      )}
    </Box>
  );
}

function ColourOrImageQuestion(props: ColourOrImageQuestionProps): JSX.Element {
  const {
    id,
    error,
    notes,
    status,
    question,
    required,
    allowImageUpload,
    backgroundColours,
    onColourChangeHandler,
    onImageChangeHandler,
    existingIdOrUrl
  } = props;

  const [colourId, setColourId] = useState(
    existingIdOrUrl?.color != null ? String(existingIdOrUrl.color) : ""
  );
  const [imageUrl, setImageUrl] = useState(existingIdOrUrl?.image || "");
  const { translate } = useTranslator();

  const handleColourChange = (colourId: string): void => {
    setImageUrl("");
    setColourId(colourId);

    if (onColourChangeHandler) onColourChangeHandler(Number(colourId));
  };

  const handleImageChange = (imageUrl: string): void => {
    setColourId("");
    setImageUrl(imageUrl);

    if (onImageChangeHandler) onImageChangeHandler(imageUrl);
  };

  const { getRootProps, getRadioProps } = useRadioGroup({
    value: `${colourId}`,
    onChange: handleColourChange
  });

  const group = getRootProps();

  return (
    <Field.Root
      my={4}
      w="full"
      id={`${id}`}
      invalid={error?.length > 0 || false}
      required={required || false}
      disabled={status === "saving"}
      className={error?.length > 0 ? "ehqQuestionError" : ""}
    >
      <Field.Label pointerEvents="none">
        <HTMLWrapper html={question} />
        {required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </Field.Label>
      <Field.HelperText color="gray.600" mt={0}>
        {notes}
      </Field.HelperText>
      <HStack
        {...group}
        mt={4}
        mb={0}
        alignItems="flex-start"
        gap={{ md: 6 }}
        justifyContent={{ sm: "space-between", md: "normal" }}
      >
        {!imageUrl &&
          backgroundColours.map(({ id, name }) => {
            const radio = getRadioProps({ value: String(id) });
            return (
              <RadioOption
                key={id}
                background={name}
                {...radio}
                dataTestId={`colourOrImageQuestion-option-${id}`}
              />
            );
          })}
        {allowImageUpload && (
          <ImageEditor
            keepOriginalImageToBeCropped
            disabled={status === "saving"}
            onCropComplete={handleImageChange}
            onDelete={() => {
              onImageChangeHandler(null as any);
              setImageUrl("");
            }}
            existingImageUrl={imageUrl}
            data-testid="colourOrImageQuestion-imageEditor"
          />
        )}
      </HStack>
      {error?.length > 0 && (
        <Field.ErrorText data-testid="colourOrImageQuestion-errorMessage">
          <Field.ErrorIcon />
          {translate(`errors.${error}`)}
        </Field.ErrorText>
      )}
    </Field.Root>
  );
}

export type { ColourOrImageQuestionProps };
export { ColourOrImageQuestion };
